/**
 * Client auth helpers — cookie-based session model.
 *
 * JWTs live exclusively in httpOnly cookies set by the server
 * (`/api/auth/login`, `/api/auth/refresh`). JavaScript can neither read
 * nor needs to attach them: same-origin fetches and the WebSocket
 * upgrade carry the cookies automatically. The only things kept in
 * localStorage are the (non-sensitive) user profile snapshot and the
 * access-token expiry timestamp used to schedule proactive refreshes.
 */

import { authedFetch } from './authed-fetch';

export interface AuthUser {
  id: number;
  email: string;
  username: string;
  role: string;
  firstName?: string;
  lastName?: string;
}

const USER_KEY = 'user';
const EXPIRY_KEY = 'accessTokenExpiresAt';
// Legacy keys from the pre-cookie localStorage token era — always purge.
const LEGACY_KEYS = ['accessToken', 'refreshToken'];

/** Remove pre-cookie era tokens from localStorage (runs once at import). */
for (const key of LEGACY_KEYS) {
  try { localStorage.removeItem(key); } catch {}
}

/**
 * Record when the current access token expires (ms epoch), from the
 * `accessTokenExpiresInMs` field returned by login/refresh endpoints.
 */
export function setAccessTokenExpiry(expiresInMs: number | undefined | null): void {
  if (typeof expiresInMs === 'number' && expiresInMs > 0) {
    localStorage.setItem(EXPIRY_KEY, String(Date.now() + expiresInMs));
  }
}

export function getAuthUser(): AuthUser | null {
  const userStr = localStorage.getItem(USER_KEY);
  if (!userStr) return null;
  try {
    return JSON.parse(userStr) as AuthUser;
  } catch {
    return null;
  }
}

export function setAuthUser(user: AuthUser): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function isAuthenticated(): boolean {
  return !!getAuthUser();
}

export function clearAuth(): void {
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(EXPIRY_KEY);
  for (const key of LEGACY_KEYS) {
    localStorage.removeItem(key);
  }
}

let refreshPromise: Promise<boolean> | null = null;
const tokenRefreshListeners: Array<() => void> = [];

export function onTokenRefresh(listener: () => void): () => void {
  tokenRefreshListeners.push(listener);
  return () => {
    const idx = tokenRefreshListeners.indexOf(listener);
    if (idx !== -1) tokenRefreshListeners.splice(idx, 1);
  };
}

/**
 * Rotate the session via the httpOnly refreshToken cookie. The server
 * sets fresh cookies on success; no tokens ever touch JavaScript.
 */
export async function refreshAuthToken(): Promise<boolean> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        clearAuth();
        return false;
      }

      const data = await response.json().catch(() => ({}));
      setAccessTokenExpiry(data?.accessTokenExpiresInMs);
      for (const listener of tokenRefreshListeners) {
        try { listener(); } catch {}
      }
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Milliseconds until the current access token expires, based on the
 * expiry timestamp recorded at login/refresh (the token itself is
 * unreadable by design). Returns null when unknown.
 */
export function getTokenExpiryMs(): number | null {
  const raw = localStorage.getItem(EXPIRY_KEY);
  if (!raw) return null;
  const expiresAt = Number(raw);
  if (!Number.isFinite(expiresAt)) return null;
  return expiresAt - Date.now();
}

/**
 * Fetch with auth. Cookies ride along automatically; this wrapper only
 * remains so call sites keep a single "authed fetch" entry point.
 * Delegates to authedFetch so state-changing requests also carry the
 * X-CSRF-Token header required by the server's CSRF middleware.
 */
export function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  return authedFetch(url, options);
}
